import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  CreditCardIcon,
  TruckIcon,
  MapPinIcon,
  ShieldCheckIcon,
  CheckCircleIcon,
  ClockIcon,
  PhoneIcon,
  LockClosedIcon,
} from '@heroicons/react/24/outline';

const CheckoutPage = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [orderCompleted, setOrderCompleted] = useState(false);
  const [formData, setFormData] = useState({
    // Shipping info
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postalCode: '',
    // Payment info
    cardNumber: '',
    cardName: '',
    expiry: '',
    cvv: '',
    // Delivery options
    deliveryOption: 'standard',
    installments: '1'
  });

  const cartItems = [
    {
      id: 1,
      name: 'Milano Executive Desk',
      price: 19999,
      quantity: 1,
      image: 'https://images.unsplash.com/photo-1631679706909-1844bbd07221?w=100&h=100&fit=crop'
    },
    {
      id: 2,
      name: 'Venetian Luxury Sofa',
      price: 39999,
      quantity: 1,
      image: 'https://images.unsplash.com/photo-1567538096630-e0c55bd6374c?w=100&h=100&fit=crop'
    }
  ];

  const subtotal = cartItems.reduce((total, item) => total + (item.price * item.quantity), 0);
  const shipping = subtotal > 50000 ? 0 : 2500;
  const total = subtotal + shipping;

  const steps = [
    { id: 1, name: 'Teslimat Bilgileri', icon: MapPinIcon },
    { id: 2, name: 'Ödeme Bilgileri', icon: CreditCardIcon },
    { id: 3, name: 'Sipariş Özeti', icon: CheckCircleIcon },
  ];

  const deliveryOptions = [
    {
      id: 'standard',
      name: 'Standart Teslimat',
      time: '3-5 İş Günü',
      price: 2500,
      description: 'Normal teslimat süresi'
    },
    {
      id: 'express',
      name: 'Hızlı Teslimat',
      time: '1-2 İş Günü',
      price: 4999,
      description: 'Acil durumlar için'
    },
    {
      id: 'appointment',
      name: 'Randevulu Teslimat',
      time: 'Size Uygun Zaman',
      price: 1999,
      description: 'Belirli bir zamanda teslim'
    }
  ];

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const nextStep = () => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    } else {
      // Complete order
      setOrderCompleted(true);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  if (orderCompleted) {
    return (
      <div className="min-h-screen bg-gray-50 py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            className="text-center bg-white rounded-2xl shadow-lg p-12"
          >
            <CheckCircleIcon className="h-24 w-24 text-emerald-500 mx-auto mb-8" />
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              Siparişiniz Alındı!
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              Sipariş numaranız: <span className="font-semibold">#2024-00{Math.floor(Math.random() * 1000)}</span>
            </p>
            <div className="space-y-4 mb-8">
              <div className="flex items-center justify-center space-x-2 text-gray-600">
                <ClockIcon className="h-5 w-5" />
                <span>Tahmini teslimat: 3-5 iş günü</span>
              </div>
              <div className="flex items-center justify-center space-x-2 text-gray-600">
                <PhoneIcon className="h-5 w-5" />
                <span>Kargo takip bilgisi e-posta ile gönderilecek</span>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/profile"
                className="px-8 py-3 bg-slate-600 text-white font-semibold rounded-lg hover:bg-slate-700 transition-colors duration-300"
              >
                Siparişlerimi Görüntüle
              </Link>
              <Link
                to="/products"
                className="px-8 py-3 bg-gray-100 text-gray-700 font-semibold rounded-lg hover:bg-gray-200 transition-colors duration-300"
              >
                Alışverişe Devam Et
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-6">Teslimat Bilgileri</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ad *
                </label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  required
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-slate-500"
                  placeholder="Adınız"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Soyad *
                </label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  required
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-slate-500"
                  placeholder="Soyadınız"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  E-posta *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-slate-500"
                  placeholder="E-posta adresiniz"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Telefon *
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-slate-500"
                  placeholder="Telefon numaranız"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Adres *
              </label>
              <textarea
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                required
                rows={3}
                className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-slate-500"
                placeholder="Tam adresinizi girin"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Şehir *
                </label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  required
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-slate-500"
                  placeholder="Şehir"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Posta Kodu
                </label>
                <input
                  type="text"
                  name="postalCode"
                  value={formData.postalCode}
                  onChange={handleInputChange}
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-slate-500"
                  placeholder="Posta kodu"
                />
              </div>
            </div>

            {/* Delivery Options */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-4">
                Teslimat Seçeneği
              </label>
              <div className="space-y-3">
                {deliveryOptions.map((option) => (
                  <label key={option.id} className="flex items-center p-4 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                    <input
                      type="radio"
                      name="deliveryOption"
                      value={option.id}
                      checked={formData.deliveryOption === option.id}
                      onChange={handleInputChange}
                      className="h-4 w-4 text-slate-600 focus:ring-slate-500"
                    />
                    <div className="ml-3 flex-1">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-gray-900">{option.name}</p>
                          <p className="text-sm text-gray-600">{option.description}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-medium text-gray-900">{option.time}</p>
                          <p className="text-sm text-gray-600">
                            {option.price === 0 ? 'Ücretsiz' : `₺${option.price.toLocaleString()}`}
                          </p>
                        </div>
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-6">Ödeme Bilgileri</h3>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <div className="flex items-center space-x-2">
                <LockClosedIcon className="h-5 w-5 text-blue-600" />
                <p className="text-sm text-blue-800">
                  Ödeme bilgileriniz 256-bit SSL ile güvence altındadır
                </p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Kart Üzerindeki İsim *
              </label>
              <input
                type="text"
                name="cardName"
                value={formData.cardName}
                onChange={handleInputChange}
                required
                className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-slate-500"
                placeholder="Ad Soyad"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Kart Numarası *
              </label>
              <input
                type="text"
                name="cardNumber"
                value={formData.cardNumber}
                onChange={handleInputChange}
                required
                maxLength="19"
                className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-slate-500"
                placeholder="1234 5678 9012 3456"
              />
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Son Kullanma Tarihi *
                </label>
                <input
                  type="text"
                  name="expiry"
                  value={formData.expiry}
                  onChange={handleInputChange}
                  required
                  maxLength="5"
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-slate-500"
                  placeholder="MM/YY"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  CVV *
                </label>
                <input
                  type="text"
                  name="cvv"
                  value={formData.cvv}
                  onChange={handleInputChange}
                  required
                  maxLength="4"
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-slate-500"
                  placeholder="123"
                />
              </div>
            </div>

            {/* Installments */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Taksit Seçeneği
              </label>
              <select
                name="installments"
                value={formData.installments}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-slate-500"
              >
                <option value="1">Tek Çekim</option>
                <option value="3">3 Taksit</option>
                <option value="6">6 Taksit</option>
                <option value="9">9 Taksit</option>
                <option value="12">12 Taksit</option>
              </select>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-6">Sipariş Özeti</h3>
            
            {/* Order Summary */}
            <div className="bg-gray-50 rounded-lg p-6">
              <h4 className="font-semibold text-gray-900 mb-4">Teslimat Bilgileri</h4>
              <div className="space-y-2 text-sm">
                <p><span className="font-medium">Ad Soyad:</span> {formData.firstName} {formData.lastName}</p>
                <p><span className="font-medium">E-posta:</span> {formData.email}</p>
                <p><span className="font-medium">Telefon:</span> {formData.phone}</p>
                <p><span className="font-medium">Adres:</span> {formData.address}, {formData.city}</p>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-6">
              <h4 className="font-semibold text-gray-900 mb-4">Ödeme Bilgileri</h4>
              <div className="space-y-2 text-sm">
                <p><span className="font-medium">Kart:</span> **** **** **** {formData.cardNumber.slice(-4)}</p>
                <p><span className="font-medium">Taksit:</span> {formData.installments} {formData.installments === '1' ? 'Tek Çekim' : 'Taksit'}</p>
              </div>
            </div>

            <div className="border-t border-gray-200 pt-6">
              <div className="flex items-center space-x-2 text-sm text-gray-600 mb-4">
                <input type="checkbox" required className="h-4 w-4 text-slate-600 focus:ring-slate-500" />
                <span>
                  <a href="#" className="text-slate-600 hover:underline">Kullanım şartlarını</a> ve 
                  <a href="#" className="text-slate-600 hover:underline"> gizlilik politikasını</a> kabul ediyorum
                </span>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl lg:text-5xl font-luxury font-bold text-gray-900 mb-4">
            Ödeme
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Siparişinizi tamamlamak için birkaç adım kaldı
          </p>
        </motion.div>

        {/* Progress Steps */}
        <div className="mb-12">
          <div className="flex items-center justify-center space-x-8">
            {steps.map((step, index) => (
              <div key={step.id} className="flex items-center">
                <div className={`flex items-center justify-center w-12 h-12 rounded-full border-2 ${
                  currentStep >= step.id
                    ? 'border-slate-600 bg-slate-600 text-white'
                    : 'border-gray-300 text-gray-400'
                }`}>
                  <step.icon className="h-6 w-6" />
                </div>
                <span className={`ml-3 text-sm font-medium ${
                  currentStep >= step.id ? 'text-slate-600' : 'text-gray-400'
                }`}>
                  {step.name}
                </span>
                {index < steps.length - 1 && (
                  <div className={`ml-8 w-16 h-0.5 ${
                    currentStep > step.id ? 'bg-slate-600' : 'bg-gray-300'
                  }`} />
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="bg-white rounded-2xl shadow-lg p-8"
            >
              {renderStepContent()}

              {/* Navigation Buttons */}
              <div className="flex justify-between mt-8 pt-6 border-t border-gray-200">
                <button
                  onClick={prevStep}
                  disabled={currentStep === 1}
                  className="px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Geri
                </button>
                <button
                  onClick={nextStep}
                  className="px-6 py-3 bg-slate-600 text-white rounded-lg hover:bg-slate-700 transition-colors duration-300"
                >
                  {currentStep === 3 ? 'Siparişi Tamamla' : 'İleri'}
                </button>
              </div>
            </motion.div>
          </div>

          {/* Order Summary Sidebar */}
          <div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-white rounded-2xl shadow-lg p-6 sticky top-6"
            >
              <h3 className="text-lg font-semibold text-gray-900 mb-6">Sipariş Özeti</h3>
              
              {/* Cart Items */}
              <div className="space-y-4 mb-6">
                {cartItems.map((item) => (
                  <div key={item.id} className="flex items-center space-x-3">
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-12 h-12 object-cover rounded-lg"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{item.name}</p>
                      <p className="text-sm text-gray-600">Adet: {item.quantity}</p>
                    </div>
                    <p className="text-sm font-medium text-gray-900">
                      ₺{(item.price * item.quantity).toLocaleString()}
                    </p>
                  </div>
                ))}
              </div>

              {/* Price Breakdown */}
              <div className="space-y-3 mb-6 pb-6 border-b border-gray-200">
                <div className="flex justify-between text-gray-600">
                  <span>Ara Toplam</span>
                  <span>₺{subtotal.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-gray-600">
                  <span>Kargo</span>
                  <span>
                    {shipping === 0 ? (
                      <span className="text-emerald-600">Ücretsiz</span>
                    ) : (
                      `₺${shipping.toLocaleString()}`
                    )}
                  </span>
                </div>
              </div>

              <div className="flex justify-between text-xl font-bold text-gray-900 mb-6">
                <span>Toplam</span>
                <span>₺{total.toLocaleString()}</span>
              </div>

              {/* Security Features */}
              <div className="space-y-3 text-sm text-gray-600">
                <div className="flex items-center space-x-2">
                  <ShieldCheckIcon className="h-4 w-4 text-emerald-600" />
                  <span>SSL ile güvenli ödeme</span>
                </div>
                <div className="flex items-center space-x-2">
                  <TruckIcon className="h-4 w-4 text-emerald-600" />
                  <span>Ücretsiz iade</span>
                </div>
                <div className="flex items-center space-x-2">
                  <CreditCardIcon className="h-4 w-4 text-emerald-600" />
                  <span>9 taksit imkanı</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage; 